import { useQuery, useQueryClient } from 'react-query';

import type { IConversation } from 'src/api/landlord-messages-api';
import { apiClient, queryKeys } from 'src/services/api';

const PATH = 'api/landlord/conversations';

interface IGetConversationsOptions<TSelected = IConversation[]> {
    /**
     * Enable/disable the fetching. Not sent to backend. Used to control hook trigger.
     */
    disabled?: boolean;
    /**
     * @deprecated Conversations always fetch unarchived (isArchived=false). This argument is ignored.
     */
    isArchived?: boolean | null;
    /**
     * If fetching in the context of an inbox, this is the id of the inbox.
     * Do not specify if we are not in an inbox setting.
     * Do not specify if this is the default team inbox (i.e. an inbox defined only on the client-side)
     */
    inboxId?: number;
    /**
     * If specified, only fetch conversations that are associated with leads matching these landlord user IDs.
     * Warning: empty array is not a valid value here
     */
    assigneeIds?: number[];

    select?: (data: IConversation[]) => TSelected;
}

export type IGetConversationsResponse = {
    conversations: IConversation[];
    /**
     * This API is paginated. Whether there is a next page.
     */
    has_more: boolean;
    /**
     * This API is paginated. What the server set the page size to.
     */
    page_size: number;
    /**
     * ISO timestamp of the most recent update time of a conversation in the result set.
     * Can use this value for incremental polling.
     * Only ever null if no conversations returned.
     */
    updated_at: string | null;
    /**
     * The oldest timestamp in this batch. Used for paginating backwards.
     */
    oldest_conversation_first_contact_at: string;
}

export const useConversations = <TSelected = IConversation[]>(options?: IGetConversationsOptions<TSelected>) => {
    const queryClient = useQueryClient();

    return useQuery<IConversation[], unknown, TSelected>(
        [queryKeys.conversations],
        async () => {
            const searchParams = new URLSearchParams();
            const isArchived = false;
            if (isArchived !== null) {
                searchParams.append('is_archived', isArchived.toString());
            }

            const res = await apiClient(PATH, { searchParams }).json<IGetConversationsResponse>();
            return res.conversations;
        }, {
            enabled: !options?.disabled,
            keepPreviousData: true,
            staleTime: Infinity,
            select: options?.select,
            onSuccess: (data) => {
                if (!Array.isArray(data)) { return; }
                data.forEach((conversation) => {
                    // NOTE: must be consistent with use-conversation
                    queryClient.setQueryData([queryKeys.conversations, 'lead', conversation.lead_uuid], conversation);
                });
            },
        }
    );
};
